<script setup>
/**
 * MobileCartFooter
 * Fixed bottom footer bar for mobile/tablet (< 1024px).
 * Houses cart button + conditional assortment request button.
 * Replaces FloatingCartButton + assortment FAB on small screens.
 */
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useCartStore } from '../../../app/store/cart.js'
import { useAssortmentRequestStore } from '../../../app/store/assortmentRequest.js'
import { ROUTES } from '../../../core/config/constants.js'
import ImmersiveSwitchButton from '../catalog/ImmersiveSwitchButton.vue'

const emit = defineEmits(['open-cart'])

const route = useRoute()
const router = useRouter()
const cartStore = useCartStore()
const assortmentRequestStore = useAssortmentRequestStore()

const { uniqueCount: cartCount, isEmpty: cartIsEmpty } = storeToRefs(cartStore)
const { count: niaSelectionCount, isEmpty: niaSelectionEmpty } = storeToRefs(assortmentRequestStore)

const isCatalogRoute = computed(
  () => route.name === ROUTES.CATALOG || route.name === ROUTES.AGENTS_CATALOG
)

const isAgentCatalog = computed(() => route.name === ROUTES.AGENTS_CATALOG)

const showAssortmentBtn = computed(() => isCatalogRoute.value && !niaSelectionEmpty.value)

// Quick "switch to immersive" — on the client catalog AND the agent/admin
// on-behalf catalog (the button itself targets the right immersive route).
const showImmersiveSwitch = computed(() => isCatalogRoute.value)

function navigateToAssortmentRequest() {
  if (isAgentCatalog.value) {
    router.push({
      name: ROUTES.AGENTS_ASSORTMENT_REQUEST,
      params: { clientMakat: route.params.clientMakat },
    })
  } else {
    router.push({ name: ROUTES.ASSORTMENT_REQUEST })
  }
}
</script>

<template>
  <div class="mobile-cart-footer">
    <!-- Quick switch to immersive catalog -->
    <ImmersiveSwitchButton
      v-if="showImmersiveSwitch"
      v-tutorial="
        isCatalogRoute
          ? {
              id: 'catalog-mobile-immersive',
              text: 'רוצים חוויית קנייה מהירה? כפתור זה מעביר לקטלוג האימרסיבי — מוצר אחרי מוצר במסך מלא',
              importance: 'info',
              showOnce: true,
              autoTrigger: false,
              group: 'catalog-mobile-intro',
              step: 5,
            }
          : null
      "
      variant="footer"
    />

    <!-- Assortment Request Button (conditional) -->
    <Transition name="footer-fade">
      <button
        v-if="showAssortmentBtn"
        class="footer-btn footer-btn--assortment"
        @click="navigateToAssortmentRequest"
      >
        <svg
          viewBox="0 0 24 24"
          width="20"
          height="20"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="12" y1="18" x2="12" y2="12" />
          <line x1="9" y1="15" x2="15" y2="15" />
        </svg>
        <span class="footer-btn-label">פתיחת מגוון</span>
        <span class="footer-badge--assortment">{{ niaSelectionCount }}</span>
      </button>
    </Transition>

    <!-- Cart Button (always) -->
    <button
      v-tutorial="
        isCatalogRoute
          ? {
              id: 'catalog-mobile-cart',
              text: 'כל המוצרים שהוספתם מחכים כאן — לחצו לצפייה בעגלה ולסיום ההזמנה',
              importance: 'important',
              showOnce: true,
              autoTrigger: false,
              group: 'catalog-mobile-intro',
              step: 4,
            }
          : null
      "
      class="footer-btn footer-btn--cart"
      :class="{ 'is-empty': cartIsEmpty }"
      @click="$emit('open-cart')"
    >
      <span v-if="cartCount > 0" class="footer-cart-count">
        {{ cartCount > 99 ? '99+' : cartCount }}
      </span>
      <svg
        viewBox="0 0 24 24"
        width="22"
        height="22"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <circle cx="9" cy="21" r="1" />
        <circle cx="20" cy="21" r="1" />
        <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6" />
      </svg>
      <span class="footer-btn-label">עגלה</span>
    </button>
  </div>
</template>

<style scoped>
.mobile-cart-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: var(--mobile-footer-height);
  padding-bottom: env(safe-area-inset-bottom, 0px);
  background: var(--gradient-header);
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: var(--spacing-sm);
  padding-left: var(--spacing-md);
  padding-right: var(--spacing-md);
  box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.15);
}

/* Hide on desktop */
@media (min-width: 1024px) {
  .mobile-cart-footer {
    display: none;
  }
}

/* ── Buttons ── */
.footer-btn {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: 8px 16px;
  border: none;
  border-radius: var(--border-radius-lg);
  font-family: var(--font-family);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition:
    opacity var(--transition-fast),
    background var(--transition-fast);
  position: relative;
}

.footer-btn-label {
  white-space: nowrap;
}

/* ── Cart Button ── */
.footer-btn--cart {
  background: transparent;
  color: white;
  border-radius: 0;
  padding: 0 16px;
  align-self: stretch;
  margin-right: auto;
  border-inline-start: 1px solid rgba(255, 255, 255, 0.25);
}

.footer-btn--cart:active {
  background: rgba(255, 255, 255, 0.1);
}

.footer-btn--cart.is-empty {
  opacity: 0.5;
}

/* Cart count (inline, light green) */
.footer-cart-count {
  color: #86efac;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  margin-inline-end: var(--spacing-xs);
}

/* ── Assortment Button ── */
.footer-btn--assortment {
  background: var(--color-success);
  color: white;
}

.footer-btn--assortment:active {
  background: #059669;
}

/* Assortment badge */
.footer-badge--assortment {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background: white;
  color: var(--color-success);
  border-radius: var(--border-radius-full);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
}

/* ── Transitions ── */
.footer-fade-enter-active {
  transition: all 0.25s ease-out;
}
.footer-fade-leave-active {
  transition: all 0.2s ease-in;
}
.footer-fade-enter-from,
.footer-fade-leave-to {
  opacity: 0;
  transform: scale(0.8);
}

.badge-pop-enter-active {
  animation: badgePop 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.badge-pop-leave-active {
  animation: badgePop 0.2s ease-out reverse;
}

@keyframes badgePop {
  0% {
    opacity: 0;
    transform: scale(0);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
